/**
 * @fileoverview Capa de Lógica de Negocio (Business Logic - BL) para la dinámica 'Rasca y Gana - Optimo Ópticas' (Tile ID 49).
 * Provee normalización, sanitización estricta, enmascaramiento conforme a LFPDPPP, telemetría de embudo y
 * gestión de persistencia multi-cupón con auto-purga reactiva basada en tiempo de vida (TTL) en localStorage.
 * 
 * @module utilities/BL/OptimoOpticas
 */

import { ValidateSqlInjection } from './Security';
import { GetSessionData } from './Sessions';
import {
    GetPublicConfig as DLGetPublicConfig,
    CheckDuplicate as DLCheckDuplicate,
    SubmitLead as DLSubmitLead,
    RecoverCoupon as DLRecoverCoupon,
    TrackMetric as DLTrackMetric,
    LogScreenshot as DLLogScreenshot,
    MarkCouponScratched as DLMarkCouponScratched,
    GetAdminConfig as DLGetAdminConfig,
    UpdateAdminConfig as DLUpdateAdminConfig,
    GetAdminParticipants as DLGetAdminParticipants,
    GetAdminStats as DLGetAdminStats,
    RedeemCoupon as DLRedeemCoupon,
    DisqualifyParticipant as DLDisqualifyParticipant,
    UndisqualifyParticipant as DLUndisqualifyParticipant,
    ResetDynamic as DLResetDynamic,
    SetScreenVisibility as DLSetScreenVisibility,
    GetExportExcelUrl as DLGetExportExcelUrl,
    SendQROptimoOpticas as DLSendQROptimoOpticas
} from '../DL/OptimoOpticas';

// =============================================================================
// CONSTANTES DE ALMACENAMIENTO Y TELEMETRÍA
// =============================================================================

const STORAGE_KEY = 'psl_optopticas_my_coupons';
const SESSION_STORAGE_KEY = 'psl_optopticas_session_id';

// =============================================================================
// ACCESO SEGURO A ALMACENAMIENTO (SSR / BROWSER / TESTING)
// =============================================================================

const getLocalStorage = () => {
    try {
        if (typeof window !== 'undefined' && window.localStorage) return window.localStorage;
        if (typeof localStorage !== 'undefined') return localStorage;
    } catch (e) {
        // En caso de restricciones de privacidad en modo incógnito
    }
    return null;
};

const getSessionStorage = () => {
    try {
        if (typeof window !== 'undefined' && window.sessionStorage) return window.sessionStorage;
        if (typeof sessionStorage !== 'undefined') return sessionStorage;
    } catch (e) {
        // En caso de restricciones de privacidad en modo incógnito
    }
    return null;
};

// =============================================================================
// NORMALIZACIÓN, SEGURIDAD Y PRIVACIDAD (LFPDPPP)
// =============================================================================

/**
 * Normaliza y extrae exactamente los 10 dígitos numéricos de un número telefónico.
 * Maneja prefijos internacionales mexicanos (+52, 52, 521).
 * 
 * @function NormalizePhone
 * @param {string|number} phone - Teléfono ingresado por el aficionado.
 * @returns {string} Teléfono a 10 dígitos numéricos o cadena vacía.
 */
const NormalizePhone = (phone) => {
    if (!phone) return '';
    let digits = String(phone).replace(/\D/g, '');

    // Formato México con código de país 52 (12 dígitos numéricos: 52 + 10 dígitos)
    if (digits.length === 12 && digits.startsWith('52')) {
        return digits.slice(2);
    }

    // Formato México celular antiguo con 521 (13 dígitos: 521 + 10 dígitos)
    if (digits.length === 13 && digits.startsWith('521')) {
        return digits.slice(3);
    }

    // Cualquier prefijo internacional con código 52 que exceda 10 dígitos
    if (digits.length > 10 && digits.startsWith('52')) {
        return digits.slice(-10);
    }

    return digits;
};

/**
 * Normaliza una dirección de correo electrónico eliminando espacios y convirtiendo a minúsculas.
 * 
 * @function NormalizeEmail
 * @param {string} email - Correo ingresado por el aficionado.
 * @returns {string} Correo normalizado.
 */
const NormalizeEmail = (email) => {
    if (!email) return '';
    return String(email).trim().toLowerCase();
};

/**
 * Enmascara el nombre completo para visualización sutil conforme a la LFPDPPP (ej: 'J*** P****').
 * 
 * @function MaskName
 * @param {string} name - Nombre completo del usuario.
 * @returns {string} Nombre enmascarado.
 */
const MaskName = (name) => {
    if (!name || typeof name !== 'string') return '';
    return name
        .trim()
        .split(/\s+/)
        .map(word => {
            if (word.length <= 1) return word;
            return word[0] + '*'.repeat(Math.max(1, word.length - 1));
        })
        .join(' ');
};

/**
 * Enmascara una dirección de correo electrónico conforme a la LFPDPPP (ej: 'j***@mail.com').
 * 
 * @function MaskEmail
 * @param {string} email - Correo electrónico a enmascarar.
 * @returns {string} Correo enmascarado.
 */
const MaskEmail = (email) => {
    if (!email || typeof email !== 'string' || !email.includes('@')) return email || '';
    const [user, domain] = email.trim().toLowerCase().split('@');
    if (!user) return `***@${domain || ''}`;
    return `${user[0]}***@${domain}`;
};

/**
 * Enmascara un número telefónico mostrando únicamente los últimos 4 dígitos (ej: '******1234').
 * 
 * @function MaskPhone
 * @param {string} phone - Teléfono a enmascarar.
 * @returns {string} Teléfono enmascarado.
 */
const MaskPhone = (phone) => {
    if (!phone) return '';
    const clean = String(phone).replace(/\D/g, '');
    if (clean.length < 4) return clean;
    return `******${clean.slice(-4)}`;
};

/**
 * Enmascara de forma integral los datos personales del participante para visualización segura.
 * Acepta tanto parámetros individuales como un objeto { name, email, phone }.
 * 
 * @function MaskUserData
 * @param {string|Object} nameOrObj - Nombre del participante o bien objeto con datos personales.
 * @param {string} [emailParam] - Correo electrónico si se usan argumentos posicionales.
 * @param {string} [phoneParam] - Teléfono si se usan argumentos posicionales.
 * @returns {{name: string, email: string, phone: string}} Datos enmascarados de forma segura.
 */
const MaskUserData = (nameOrObj, emailParam, phoneParam) => {
    let name = nameOrObj;
    let email = emailParam;
    let phone = phoneParam;

    if (nameOrObj && typeof nameOrObj === 'object' && !Array.isArray(nameOrObj)) {
        name = nameOrObj.name;
        email = nameOrObj.email;
        phone = nameOrObj.phone;
    }

    return {
        name: MaskName(name),
        email: MaskEmail(email),
        phone: MaskPhone(phone)
    };
};

/**
 * Valida los campos del formulario de registro móvil del aficionado y sanitiza contra inyecciones SQL.
 * 
 * @async
 * @function ValidateLeadForm
 * @param {Object} formData - { name, email, phone, acceptedTerms, acceptedMarketing, trafficSource }
 * @returns {Promise<{ isValid: boolean, error: string|null, sanitizedData: Object|null }>}
 */
const ValidateLeadForm = async (formData) => {
    if (!formData || typeof formData !== 'object') {
        return { isValid: false, error: 'Por favor completa el formulario de registro.', sanitizedData: null };
    }

    const rawName = formData.name ? String(formData.name).trim() : '';
    if (!rawName || rawName.length < 3) {
        return { isValid: false, error: 'Por favor ingresa tu nombre completo (mínimo 3 caracteres).', sanitizedData: null };
    }

    const rawEmail = NormalizeEmail(formData.email);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!rawEmail || !emailRegex.test(rawEmail)) {
        return { isValid: false, error: 'Por favor ingresa un correo electrónico válido.', sanitizedData: null };
    }

    const cleanPhone = NormalizePhone(formData.phone);
    if (!cleanPhone || cleanPhone.length !== 10) {
        return { isValid: false, error: 'Por favor ingresa tu número telefónico a 10 dígitos.', sanitizedData: null };
    }

    if (!formData.acceptedTerms) {
        return { isValid: false, error: 'Debes aceptar los términos y condiciones para continuar.', sanitizedData: null };
    }

    // Sanitización preventiva contra ataques de inyección SQL
    const isNameDangerous = await ValidateSqlInjection(rawName);
    const isEmailDangerous = await ValidateSqlInjection(rawEmail);
    const isPhoneDangerous = await ValidateSqlInjection(cleanPhone);
    const isTrafficDangerous = formData.trafficSource ? await ValidateSqlInjection(String(formData.trafficSource)) : false;

    if (isNameDangerous || isEmailDangerous || isPhoneDangerous || isTrafficDangerous) {
        return { isValid: false, error: 'Se detectaron caracteres no permitidos en el formulario.', sanitizedData: null };
    }

    const sanitizedData = {
        ...formData,
        name: rawName,
        email: rawEmail,
        phone: cleanPhone,
        acceptedTerms: Boolean(formData.acceptedTerms),
        acceptedMarketing: Boolean(formData.acceptedMarketing)
    };

    return { isValid: true, error: null, sanitizedData };
};

// =============================================================================
// SESIÓN DE USUARIO Y TELEMETRÍA DE EMBUDO
// =============================================================================

/**
 * Obtiene o inicializa el identificador único de sesión anónimo en sessionStorage.
 * 
 * @function GetSessionId
 * @returns {string} Identificador de sesión (ej: 'sess_abc1234_1728392100000').
 */
const GetSessionId = () => {
    try {
        const sessStorage = getSessionStorage();
        if (sessStorage) {
            let sessionId = sessStorage.getItem(SESSION_STORAGE_KEY);
            if (!sessionId) {
                sessionId = 'sess_' + Math.random().toString(36).substring(2, 9) + '_' + Date.now();
                sessStorage.setItem(SESSION_STORAGE_KEY, sessionId);
            }
            return sessionId;
        }
    } catch (e) {
        console.warn("Advertencia en GetSessionId (sessionStorage inaccesible):", e);
    }
    return 'sess_' + Math.random().toString(36).substring(2, 9) + '_' + Date.now();
};

/**
 * Registra un evento de telemetría del embudo de conversión en el Data Layer y despacha a dataLayer (GTM/GA4).
 * No expone ni transmite datos personales sensibles (PII).
 * 
 * @async
 * @function TrackMetric
 * @param {string} eventName - Nombre del evento (ej: 'page_view', 'form_start', 'game_start', 'game_complete', etc.).
 * @param {string|null} [source=null] - Origen del tráfico ('direct', 'qr_screen', etc.).
 * @returns {Promise<{success: boolean, message?: string}>}
 */
const TrackMetric = async (eventName, source = null) => {
    const sessionId = GetSessionId();

    // Despacho a Google Tag Manager / dataLayer si está definido en el DOM
    try {
        if (typeof window !== 'undefined' && window.dataLayer && Array.isArray(window.dataLayer)) {
            window.dataLayer.push({
                event: `psl_optopticas_${eventName}`,
                sessionId,
                source: source || 'direct',
                timestamp: new Date().toISOString()
            });
        }
    } catch (e) {
        console.warn("Fallo no bloqueante al registrar telemetría en dataLayer:", e);
    }

    // Invocación a Data Layer de la plataforma
    try {
        return await DLTrackMetric(eventName, sessionId, source);
    } catch (error) {
        console.error("Error en BL TrackMetric:", error);
        return { success: false, message: 'Error al registrar métrica.' };
    }
};

// =============================================================================
// MOTOR MULTI-CUPÓN Y AUTO-PURGA TTL EN LOCALSTORAGE
// =============================================================================

/**
 * Compara la antigüedad de cada cupón almacenado contra el tiempo de vida (TTL) configurado en días
 * y purga automáticamente los cupones que hayan vencido.
 * 
 * @function PurgeExpiredLocalCoupons
 * @param {number} [storageExpirationDays=7] - Días de vigencia del almacenamiento local (por defecto 7).
 * @returns {Array<Object>} Lista de cupones válidos que permanecen en el almacenamiento.
 */
const PurgeExpiredLocalCoupons = (storageExpirationDays = 7) => {
    try {
        const storage = getLocalStorage();
        if (!storage) return [];

        const raw = storage.getItem(STORAGE_KEY);
        if (!raw) return [];

        let coupons = [];
        try {
            coupons = JSON.parse(raw);
        } catch {
            storage.removeItem(STORAGE_KEY);
            return [];
        }

        if (!Array.isArray(coupons)) {
            storage.removeItem(STORAGE_KEY);
            return [];
        }

        const days = Number(storageExpirationDays) > 0 ? Number(storageExpirationDays) : 7;
        const ttlMs = days * 86400 * 1000;
        const now = Date.now();

        const validCoupons = coupons.filter(c => {
            if (!c || !c.couponCode) return false;
            const dateStr = c.savedAtUtc || c.savedAt;
            if (!dateStr) return false;
            const timestamp = new Date(dateStr).getTime();
            if (isNaN(timestamp)) return false;
            const elapsed = now - timestamp;
            return elapsed >= 0 && elapsed <= ttlMs;
        });

        if (validCoupons.length !== coupons.length) {
            storage.setItem(STORAGE_KEY, JSON.stringify(validCoupons));
        }

        return validCoupons;
    } catch (error) {
        console.error("Error al purgar cupones expirados en localStorage:", error);
        return [];
    }
};

/**
 * Obtiene los cupones ganados vigentes guardados en el dispositivo tras disparar la auto-purga TTL.
 * 
 * @function GetLocalCoupons
 * @param {number} [storageExpirationDays=7] - Días de vigencia para el descarte TTL.
 * @returns {Array<Object>} Lista de cupones vigentes.
 */
const GetLocalCoupons = (storageExpirationDays = 7) => {
    return PurgeExpiredLocalCoupons(storageExpirationDays);
};

/**
 * Almacena o actualiza un cupón en el almacenamiento local multi-cupón con marca de tiempo UTC.
 * 
 * @function SaveCouponToLocal
 * @param {Object} couponData - Datos del cupón ({ couponCode, discountDescription, winnerRank, ... }).
 * @returns {boolean} True si se guardó exitosamente, False en caso contrario.
 */
const SaveCouponToLocal = (couponData) => {
    try {
        if (!couponData || !couponData.couponCode) return false;
        const storage = getLocalStorage();
        if (!storage) return false;

        const currentCoupons = GetLocalCoupons(couponData.storageExpirationDays || 7);
        const nowIso = new Date().toISOString();

        const newRecord = {
            ...couponData,
            savedAtUtc: couponData.savedAtUtc || nowIso,
            updatedAtUtc: nowIso
        };

        const existingIndex = currentCoupons.findIndex(c => c.couponCode === couponData.couponCode);
        if (existingIndex >= 0) {
            currentCoupons[existingIndex] = {
                ...currentCoupons[existingIndex],
                ...newRecord,
                savedAtUtc: currentCoupons[existingIndex].savedAtUtc || newRecord.savedAtUtc
            };
        } else {
            currentCoupons.unshift(newRecord);
        }

        storage.setItem(STORAGE_KEY, JSON.stringify(currentCoupons));
        return true;
    } catch (error) {
        console.error("Error en SaveCouponToLocal:", error);
        return false;
    }
};

/**
 * Elimina un cupón específico del almacenamiento local por su código de folio.
 * 
 * @function RemoveCouponFromLocal
 * @param {string} couponCode - Folio del cupón a remover.
 * @returns {boolean} True si se removió exitosamente.
 */
const RemoveCouponFromLocal = (couponCode) => {
    try {
        if (!couponCode) return false;
        const storage = getLocalStorage();
        if (!storage) return false;

        const current = GetLocalCoupons();
        const filtered = current.filter(c => c.couponCode !== couponCode);
        storage.setItem(STORAGE_KEY, JSON.stringify(filtered));
        return true;
    } catch (error) {
        console.error("Error en RemoveCouponFromLocal:", error);
        return false;
    }
};

/**
 * Elimina todos los cupones almacenados localmente en el dispositivo.
 * 
 * @function ClearAllLocalCoupons
 * @returns {boolean} True si se limpió el almacenamiento.
 */
const ClearAllLocalCoupons = () => {
    try {
        const storage = getLocalStorage();
        if (storage) {
            storage.removeItem(STORAGE_KEY);
        }
        return true;
    } catch (error) {
        console.error("Error en ClearAllLocalCoupons:", error);
        return false;
    }
};

// =============================================================================
// HELPERS ADMINISTRATIVOS Y DE OPERACIÓN
// =============================================================================

/**
 * Obtiene el nombre del operador administrador autenticado en la sesión, con fallback a 'Team Interaktia'.
 * 
 * @async
 * @function GetAdminOperatorName
 * @returns {Promise<string>} Nombre o correo del operador.
 */
const GetAdminOperatorName = async () => {
    try {
        const sessionRes = await GetSessionData(true);
        if (sessionRes && sessionRes.success && sessionRes.data) {
            const user = sessionRes.data;
            const op = user.name || user.fullName || user.fullname || user.email || user.username || user.user;
            if (op && typeof op === 'string' && op.trim().length > 0) {
                return op.trim();
            }
        }
    } catch (e) {
        console.warn("No se pudo obtener el nombre de operador de la sesión:", e);
    }
    return "Team Interaktia";
};

/**
 * Descalifica a un participante envolviendo automáticamente el operador de sesión.
 * 
 * @async
 * @function DisqualifyParticipant
 * @param {number} participantId - ID numérico del participante.
 * @param {string} reason - Motivo justificado de la descalificación.
 * @param {string|null} [operator=null] - Operador opcional.
 * @returns {Promise<Object>} Resultado de la operación.
 */
const DisqualifyParticipant = async (participantId, reason, operator = null) => {
    const finalOperator = operator || await GetAdminOperatorName();
    return await DLDisqualifyParticipant(participantId, reason, finalOperator);
};

/**
 * Remueve la descalificación de un participante envolviendo el operador de sesión.
 * 
 * @async
 * @function UndisqualifyParticipant
 * @param {number} participantId - ID numérico del participante.
 * @param {string|null} [operator=null] - Operador opcional.
 * @returns {Promise<Object>} Resultado de la operación.
 */
const UndisqualifyParticipant = async (participantId, operator = null) => {
    const finalOperator = operator || await GetAdminOperatorName();
    return await DLUndisqualifyParticipant(participantId, finalOperator);
};

/**
 * Realiza el canje de un cupón en sucursal o estadio envolviendo automáticamente el operador de sesión.
 * 
 * @async
 * @function RedeemCoupon
 * @param {Object|string} payload - Folio o payload de canje.
 * @param {string|null} [operator=null] - Operador opcional.
 * @returns {Promise<Object>} Resultado de la operación.
 */
const RedeemCoupon = async (payload, operator = null) => {
    const finalOperator = operator || await GetAdminOperatorName();
    let finalPayload = {};
    if (typeof payload === 'string') {
        const clean = payload.replace(/[\r\n\t]+/g, '').trim();
        finalPayload = { 
            couponCode: clean,
            verificationToken: clean, 
            operator: finalOperator 
        };
    } else if (payload && typeof payload === 'object') {
        const cCode = payload.couponCode ? String(payload.couponCode).replace(/[\r\n\t]+/g, '').trim() : '';
        const vToken = payload.verificationToken ? String(payload.verificationToken).replace(/[\r\n\t]+/g, '').trim() : cCode;
        finalPayload = { 
            ...payload, 
            couponCode: cCode,
            verificationToken: vToken,
            operator: payload.operator || finalOperator 
        };
    }
    return await DLRedeemCoupon(finalPayload);
};

/**
 * Sintetizador acústico nativo Web Audio API para feedback sonoro inmediato en check-in staff (0ms latencia).
 * Soporta retroalimentación diferenciada para éxito, advertencia de reuso y error/rechazo.
 * 
 * @function PlayCheckinSound
 * @param {'success' | 'warning' | 'error'} [type='success'] - Tipo de señal sonora a sintetizar.
 */
const PlayCheckinSound = (type = 'success') => {
    try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;
        const ctx = new AudioContext();

        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);

        const now = ctx.currentTime;

        if (type === 'success') {
            // Oscilador sine ascendente brillante (D5 587.33Hz -> A5 880.00Hz, duración 0.35s)
            osc.type = 'sine';
            osc.frequency.setValueAtTime(587.33, now); // D5
            osc.frequency.exponentialRampToValueAtTime(880.00, now + 0.15); // A5
            gain.gain.setValueAtTime(0.3, now);
            gain.gain.exponentialRampToValueAtTime(0.01, now + 0.35);
            osc.start(now);
            osc.stop(now + 0.35);
        } else if (type === 'warning') {
            // Oscilador triangle doble tono ámbar de advertencia (440Hz -> 440Hz)
            osc.type = 'triangle';
            osc.frequency.setValueAtTime(440, now);
            gain.gain.setValueAtTime(0.3, now);
            gain.gain.exponentialRampToValueAtTime(0.01, now + 0.12);
            osc.start(now);
            osc.stop(now + 0.12);

            const osc2 = ctx.createOscillator();
            const gain2 = ctx.createGain();
            osc2.connect(gain2);
            gain2.connect(ctx.destination);
            osc2.type = 'triangle';
            osc2.frequency.setValueAtTime(440, now + 0.16);
            gain2.gain.setValueAtTime(0.3, now + 0.16);
            gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.32);
            osc2.start(now + 0.16);
            osc2.stop(now + 0.32);
        } else {
            // Oscilador sawtooth grave descendente (220Hz -> 110Hz, duración 0.4s)
            osc.type = 'sawtooth';
            osc.frequency.setValueAtTime(220, now);
            osc.frequency.exponentialRampToValueAtTime(110, now + 0.35);
            gain.gain.setValueAtTime(0.25, now);
            gain.gain.exponentialRampToValueAtTime(0.01, now + 0.4);
            osc.start(now);
            osc.stop(now + 0.4);
        }
    } catch (e) {
        console.warn("Audio playback no soportado o bloqueado por navegador:", e);
    }
};

/**
 * Formatea una fecha UTC al estándar de fecha y hora de México ('es-MX').
 * 
 * @function formatMexicoDate
 * @param {string|Date} dateStr - Cadena de fecha ISO UTC o timestamp.
 * @returns {string} Fecha formateada en español México (ej: '09/10/2026, 14:30:15 hrs').
 */
const formatMexicoDate = (dateStr) => {
    if (!dateStr) return 'No disponible';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return String(dateStr);
    return date.toLocaleString('es-MX', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    }) + ' hrs';
};

// =============================================================================
// OPERACIONES PÚBLICAS Y DE PARTICIPACIÓN INTEGRADA
// =============================================================================

/**
 * Consulta la configuración pública de la dinámica desde el backend.
 * 
 * @async
 * @function GetPublicConfig
 * @returns {Promise<Object>} Configuración pública.
 */
const GetPublicConfig = async () => {
    return await DLGetPublicConfig();
};

/**
 * Comprueba duplicidad previa aplicando normalización inmediata de teléfono y correo.
 * 
 * @async
 * @function CheckDuplicate
 * @param {string} phone - Teléfono ingresado.
 * @param {string} email - Correo electrónico ingresado.
 * @returns {Promise<Object>} Resultado de duplicidad.
 */
const CheckDuplicate = async (phone, email) => {
    const cleanPhone = NormalizePhone(phone);
    const cleanEmail = NormalizeEmail(email);
    return await DLCheckDuplicate(cleanPhone, cleanEmail);
};

/**
 * Valida, sanitiza y envía el registro del aficionado. En caso de resultar ganador,
 * persiste automáticamente el cupón en el almacenamiento multi-cupón local.
 * 
 * @async
 * @function SubmitLead
 * @param {Object} formData - Datos capturados en el formulario móvil.
 * @returns {Promise<Object>} Respuesta del servidor con estado de ganador y folio.
 */
const SubmitLead = async (formData) => {
    const validation = await ValidateLeadForm(formData);
    if (!validation.isValid) {
        return { success: false, message: validation.error };
    }

    const payload = {
        ...validation.sanitizedData,
        sessionId: GetSessionId()
    };

    const response = await DLSubmitLead(payload);

    // Auto-persistencia en almacenamiento local si es ganador
    if (response && response.success && response.isWinner && response.couponCode) {
        SaveCouponToLocal({
            couponCode: response.couponCode,
            discountDescription: response.discountDescription,
            winnerRank: response.winnerRank,
            name: response.name || validation.sanitizedData.name,
            email: response.email || validation.sanitizedData.email,
            phone: response.phone || validation.sanitizedData.phone,
            storageExpirationDays: response.storageExpirationDays || 7,
            verificationToken: response.verificationToken,
            matchTitle: response.matchTitle,
            campaignTitle: response.campaignTitle,
            showPassUserData: response.showPassUserData,
            showPassQr: response.showPassQr,
            showPassLocationNote: response.showPassLocationNote,
            additionalInstructions: response.additionalInstructions
        });
    }

    return response;
};

/**
 * Recupera un cupón ganado previamente y lo resincroniza en el almacenamiento local.
 * 
 * @async
 * @function RecoverCoupon
 * @param {string} query - Correo o teléfono registrado.
 * @param {string|null} [searchType=null] - Tipo opcional de búsqueda.
 * @returns {Promise<Object>} Resultado de la recuperación.
 */
const RecoverCoupon = async (query, searchType = null) => {
    if (!query || !query.trim()) {
        return { success: false, message: 'Por favor ingresa tu correo o teléfono registrado.' };
    }

    const trimmedQuery = query.trim();
    const sessionId = GetSessionId();

    const response = await DLRecoverCoupon(trimmedQuery, searchType, sessionId);

    if (response && response.success && response.isWinner && response.couponCode) {
        SaveCouponToLocal({
            couponCode: response.couponCode,
            discountDescription: response.discountDescription,
            winnerRank: response.winnerRank,
            name: response.name,
            email: response.email,
            phone: response.phone,
            storageExpirationDays: response.storageExpirationDays || 7,
            verificationToken: response.verificationToken,
            matchTitle: response.matchTitle,
            campaignTitle: response.campaignTitle,
            showPassUserData: response.showPassUserData,
            showPassQr: response.showPassQr,
            showPassLocationNote: response.showPassLocationNote,
            additionalInstructions: response.additionalInstructions
        });
    }

    return response;
};

/**
 * Registra una descarga o captura del cupón en galería.
 * 
 * @async
 * @function LogScreenshot
 * @param {number|Object} participantId - ID del participante o payload de captura.
 * @param {string} [method='BUTTON_DOWNLOAD'] - Método detectado.
 * @returns {Promise<Object>}
 */
const LogScreenshot = async (participantId, method = 'BUTTON_DOWNLOAD') => {
    return await DLLogScreenshot(participantId, method);
};

// Delegaciones directas hacia DL para vistas administrativas
const GetAdminConfig = async () => await DLGetAdminConfig();
const UpdateAdminConfig = async (config) => await DLUpdateAdminConfig(config);
const GetAdminParticipants = async (params = {}) => await DLGetAdminParticipants(params);
const GetAdminStats = async () => await DLGetAdminStats();
const ResetDynamic = async () => await DLResetDynamic();
const SetScreenVisibility = async (visible) => await DLSetScreenVisibility(visible);
const GetExportExcelUrl = () => DLGetExportExcelUrl();
const MarkCouponScratched = async (participantId, couponCode = null) => {
    try {
        return await DLMarkCouponScratched(participantId, couponCode);
    } catch (e) {
        console.warn("Fallo no bloqueante en BL MarkCouponScratched:", e);
        return { success: false, message: 'Error al marcar cupón como descubierto.' };
    }
};

const SendQROptimoOpticas = async () => {
    try {
        return await DLSendQROptimoOpticas();
    } catch (e) {
        console.warn("Fallo no bloqueante en BL SendQROptimoOpticas:", e);
        return { success: false, message: 'Error al proyectar en pantalla.' };
    }
};

// =============================================================================
// EXPORTACIÓN MODULAR (ES6+)
// =============================================================================

export {
    STORAGE_KEY,
    SESSION_STORAGE_KEY,
    NormalizePhone,
    NormalizeEmail,
    MaskName,
    MaskEmail,
    MaskPhone,
    MaskUserData,
    ValidateLeadForm,
    GetSessionId,
    TrackMetric,
    PurgeExpiredLocalCoupons,
    GetLocalCoupons,
    SaveCouponToLocal,
    RemoveCouponFromLocal,
    ClearAllLocalCoupons,
    GetAdminOperatorName,
    DisqualifyParticipant,
    UndisqualifyParticipant,
    RedeemCoupon,
    PlayCheckinSound,
    formatMexicoDate,
    GetPublicConfig,
    CheckDuplicate,
    SubmitLead,
    RecoverCoupon,
    LogScreenshot,
    MarkCouponScratched,
    SendQROptimoOpticas,
    GetAdminConfig,
    UpdateAdminConfig,
    GetAdminParticipants,
    GetAdminStats,
    ResetDynamic,
    SetScreenVisibility,
    GetExportExcelUrl
};

const BLOptimoOpticas = {
    STORAGE_KEY,
    SESSION_STORAGE_KEY,
    NormalizePhone,
    NormalizeEmail,
    MaskName,
    MaskEmail,
    MaskPhone,
    MaskUserData,
    ValidateLeadForm,
    GetSessionId,
    TrackMetric,
    PurgeExpiredLocalCoupons,
    GetLocalCoupons,
    SaveCouponToLocal,
    RemoveCouponFromLocal,
    ClearAllLocalCoupons,
    GetAdminOperatorName,
    DisqualifyParticipant,
    UndisqualifyParticipant,
    RedeemCoupon,
    PlayCheckinSound,
    formatMexicoDate,
    GetPublicConfig,
    CheckDuplicate,
    SubmitLead,
    RecoverCoupon,
    LogScreenshot,
    MarkCouponScratched,
    SendQROptimoOpticas,
    GetAdminConfig,
    UpdateAdminConfig,
    GetAdminParticipants,
    GetAdminStats,
    ResetDynamic,
    SetScreenVisibility,
    GetExportExcelUrl
};

export default BLOptimoOpticas;
